import React, {useEffect, useMemo, useState} from 'react'
import CheckoutPaymentForm from './CheckoutPaymentForm'
import {
  useGetPaymentMethodsQuery,
  useGetPaymentSessionQuery
} from '../../services/adyenPaymentsApi'
import {useAppDispatch, useAppSelector} from '../../store/hooks'
import {selectGuestInfo} from '../../store/slices/guestInfoSlice'
import {selectBookingMetadata} from '../../store/selectors'
import {getLogger} from '../../services/logging'
import Alert from '../common/Alert'
import {getConfig} from '../../services/config'
import {
  selectCartItems,
  selectCartItemsProperty
} from '../../store/slices/shoppingCartSlice'
import {calcCartPriceSummary} from '../../services/offerUtils'
import {formatCurrency} from '../../services/utils'
import {PaymentSession, PaymentStatusValues} from '../../schema'
import currency from 'currency.js'
import useCartValidator from '../../hooks/useCartValidator'
import {
  selectPaymentStatus,
  setPaymentSessionId,
  setPaymentStatus
} from '../../store/slices/paymentSlice'
import {pushDataLayerEvent} from '../../services/dataLayer'

const logger = getLogger('CheckoutPayment')
const {environment} = getConfig()
const isProduction = environment === 'prod'

export interface CheckoutPaymentProps {
  isActive: boolean
}

const CheckoutPayment = ({isActive}: CheckoutPaymentProps) => {
  const dispatch = useAppDispatch()
  const {validate} = useCartValidator()

  // use hardcoded propertyId for testing in development environment
  const propertyId = isProduction
    ? useAppSelector(selectCartItemsProperty)
    : 'TEST'
  const paymentStatus = useAppSelector(selectPaymentStatus)
  const guestInfo = useAppSelector(selectGuestInfo)
  const cartItems = useAppSelector(selectCartItems)
  const cartPriceSummary = useMemo(() => {
    return calcCartPriceSummary(cartItems)
  }, [cartItems])
  const [hasPaymentError, setHasPaymentError] = useState<boolean>(false)
  const paymentMethodsQuery = useGetPaymentMethodsQuery(propertyId as string, {
    skip: !propertyId
  })
  const paymentMethods = paymentMethodsQuery?.data
  const bookingMetadata = useAppSelector(selectBookingMetadata)
  const amountInCents =
    cartPriceSummary.totalPrepayment && cartPriceSummary.totalPrepayment > 0
      ? currency(cartPriceSummary.totalPrepayment).multiply(100).value
      : 0
  const paymentSessionQuery = useGetPaymentSessionQuery(
    {
      amountInCents,
      isPaymentAccount: true,
      metadata: bookingMetadata,
      propertyId,
      returnUrl: window.location.href,
      shopperEmail: guestInfo.email,
      lastUpdated: guestInfo.lastUpdated
    },
    {
      skip: !isActive || !propertyId
    }
  )
  const paymentSession: PaymentSession = paymentSessionQuery?.data
  const isLoading =
    paymentMethodsQuery.isFetching || paymentSessionQuery.isFetching

  logger.info({
    message: 'Rendering checkout payment...',
    data: {paymentSession, paymentMethods, isLoading}
  })

  // trigger validation when payment session is created
  useEffect(() => {
    if (validate && paymentSession) {
      logger.info({
        message: 'Validating shopping cart upon payment session creation'
      })
      validate()
    }
  }, [paymentSession, validate])

  // refetch payment session when payment status changes to failure
  useEffect(() => {
    if (paymentStatus === PaymentStatusValues.Failure) {
      logger.warn({
        message: 'Payment failed, starting new payment session...'
      })
      paymentSessionQuery.refetch()
    }
  }, [paymentStatus])

  return (
    <div className="card card-compact bg-white border shadow">
      <div className="card-body">
        <h1 className="text-lg font-semibold">Payment</h1>

        {isActive && (
          <>
            {/* payment form */}
            {!hasPaymentError &&
              !isLoading &&
              paymentMethods &&
              paymentSession && (
                <div className="mt-4 flex flex-col gap-1">
                  {cartPriceSummary.totalPrepayment > 0 ? (
                    <Alert type="info" className="mb-2">
                      <p className="text-left text-sm">
                        A payment of{' '}
                        {formatCurrency(cartPriceSummary.totalPrepayment)} is
                        required to guarantee your stay.
                        {cartPriceSummary.balance > 0
                          ? ` The remaining balance of ${formatCurrency(
                              cartPriceSummary.balance
                            )} will be due later.`
                          : ''}{' '}
                        Please note that prepaid cards are not accepted.
                      </p>
                    </Alert>
                  ) : (
                    <Alert type="info" className="mb-2">
                      <p className="text-left text-sm">
                        Credit card details are needed to guarantee your stay.
                        Your card will not be charged.{' '}
                        <span className="font-semibold">
                          Please note that prepaid cards are not accepted.
                        </span>
                      </p>
                    </Alert>
                  )}

                  <CheckoutPaymentForm
                    paymentMethods={paymentMethods}
                    paymentSession={paymentSession}
                    onPaymentCompleted={(result: any) => {
                      const paymentSessionId = paymentSession?.paymentSessionId
                      logger.info({
                        message: 'Payment submitted and processing...',
                        data: {paymentSessionId, result}
                      })
                      pushDataLayerEvent({
                        event: 'Checkout_Payment_Submit',
                        category: 'IBE',
                        value: 1
                      })
                      dispatch(setPaymentSessionId(paymentSessionId))
                      dispatch(setPaymentStatus(PaymentStatusValues.Processing))
                    }}
                    onPaymentFailed={(error: any) => {
                      logger.error({
                        message: 'Payment failed',
                        data: {error}
                      })
                      setHasPaymentError(true)
                    }}
                  />
                </div>
              )}

            {/* loading message */}
            {!hasPaymentError && isLoading && (
              <div className="my-8 flex flex-row gap-2 items-center">
                <span className="loading loading-spinner loading-sm"></span>
                <h2 className="text-sm">Creating secure payment session...</h2>
              </div>
            )}

            {/* error with payment methods or session */}
            {!hasPaymentError &&
              !isLoading &&
              (!paymentMethods || !paymentSession) && (
                <Alert type="error" className="mt-2">
                  <p className="text-left">
                    There was an error with the payment form. If the problem
                    persists, please contact support:
                    <a
                      className="link"
                      target="_blank"
                      href="mailto:hello@placemakr.com"
                      aria-label="Email the Placemakr support team."
                    >
                      hello@placemakr.com
                    </a>
                  </p>
                </Alert>
              )}

            {/* payment error */}
            {hasPaymentError && (
              <Alert type="error">
                <p className="text-left">
                  There was an error processing your payment. Please make sure
                  your payment information is correct and try again. If the
                  problem persists, please contact support:{' '}
                  <a
                    className="link"
                    target="_blank"
                    href="mailto:hello@placemakr.com"
                    aria-label="Email the Placemakr support team."
                  >
                    hello@placemakr.com
                  </a>
                </p>
                <div className="flex flex-row justify-center items-center mt-2">
                  <button
                    className="btn btn-error btn-active mt-2 w-1/3"
                    onClick={() => {
                      // clear error state and refetch payment session
                      setHasPaymentError(false)
                      paymentSessionQuery.refetch()
                    }}
                    aria-label="Retry the payment."
                  >
                    try again
                  </button>
                </div>
              </Alert>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default CheckoutPayment
